import moment from "moment";
import {
  filter,
  sumBy,
  groupBy,
  map,
  orderBy,
  uniq,
  keyBy,
  mean,
  minBy,
  maxBy,
  findIndex,
  findLastIndex,
} from "lodash";
import { OPERATING_HOURS, formatHourLabel } from "./heatmap";
import {
  PAYMENT_METHODS,
  TABLE_KINDS,
  DELIVERY_CHANNELS,
  getPaymentAmounts,
} from "./constants";
import { prepMinutes } from "./prepTimes";

// "Panel del día" (DashboardReport.js) — indicadores inspirados en el
// Dashboard de Restaurant.pe, calculados SOLO con datos que iCard ya guarda.
// Ventas = pagos cobrados (PAID) de efectivo/tarjeta; las pérdidas (LOSS)
// van aparte y nunca suman como venta.

function orderAmount(order) {
  // Mismo criterio que getOrderPrice, pero sin explotar con pedidos
  // huérfanos (producto borrado y sin precio guardado) — ver CLAUDE.md,
  // "Order.product_data puede ser null".
  if (order.price != null) return Number(order.price);
  if (order.product_data) return Number(order.product_data.price);
  return 0;
}

export function buildDashboard(payments, orders) {
  const sales = filter(payments, (p) => p.paymentType !== "LOSS");
  const losses = filter(payments, (p) => p.paymentType === "LOSS");
  const salesById = keyBy(sales, "id");

  const totalSales = sumBy(sales, (p) => Number(p.totalPayment));
  const salesCount = sales.length;
  // Lo cobrado por cada forma de pago (un pago mixto se reparte entre las
  // suyas — ver getPaymentAmounts).
  const methodTotal = (value) => sumBy(sales, (p) => getPaymentAmounts(p)[value] || 0);
  const cashTotal = methodTotal("CASH");
  const cardTotal = methodTotal("CARD");
  const pct = (value) => (totalSales > 0 ? (value / totalSales) * 100 : 0);
  // Una entrada por forma de pago (Efectivo, Tarjeta, Yape, Plin).
  const byMethod = map(PAYMENT_METHODS, (method) => {
    const total = methodTotal(method.value);
    return { ...method, total, pct: pct(total) };
  });

  // Pedidos que forman parte de una venta cobrada del rango.
  const soldOrders = filter(orders, (o) => o.payment && salesById[o.payment]);

  // Ranking por producto.
  const byProduct = map(
    groupBy(soldOrders, (o) => o.product ?? `x-${o.id}`),
    (productOrders) => ({
      title: productOrders[0].product_data?.title || "Producto eliminado",
      quantity: productOrders.length,
      revenue: sumBy(productOrders, orderAmount),
    })
  );
  const productRevenueTotal = sumBy(byProduct, "revenue");
  const withShare = (rows) =>
    map(rows, (row) => ({
      ...row,
      share: productRevenueTotal > 0 ? (row.revenue / productRevenueTotal) * 100 : 0,
    }));
  const topByQuantity = withShare(
    orderBy(byProduct, ["quantity", "revenue"], ["desc", "desc"]).slice(0, 10)
  );
  const topByRevenue = withShare(
    orderBy(byProduct, ["revenue", "quantity"], ["desc", "desc"]).slice(0, 10)
  );

  // Por categoría (equivalente a su "Más vendidos en Cocina / Bar").
  const byCategory = withShare(
    orderBy(
      map(
        groupBy(soldOrders, (o) => o.product_data?.category_data?.title || "Sin categoría"),
        (categoryOrders, title) => ({
          title,
          quantity: categoryOrders.length,
          revenue: sumBy(categoryOrders, orderAmount),
        })
      ),
      ["revenue"],
      ["desc"]
    )
  );

  // Más vendidos por área de producción (su "Más vendidos en Cocina / Bar").
  // Solo si al menos una categoría tiene área configurada.
  const areaOf = (o) => o.product_data?.category_data?.production_area || "";
  const hasAreas = soldOrders.some((o) => areaOf(o));
  const byArea = hasAreas
    ? orderBy(
        map(
          groupBy(soldOrders, (o) => areaOf(o) || "Sin área"),
          (areaOrders, area) => {
            const areaProducts = map(
              groupBy(areaOrders, (o) => o.product ?? `x-${o.id}`),
              (productOrders) => ({
                title: productOrders[0].product_data?.title || "Producto eliminado",
                quantity: productOrders.length,
                revenue: sumBy(productOrders, orderAmount),
              })
            );
            const areaRevenue = sumBy(areaProducts, "revenue");
            return {
              area,
              quantity: areaOrders.length,
              revenue: areaRevenue,
              products: map(
                orderBy(areaProducts, ["quantity", "revenue"], ["desc", "desc"]).slice(0, 5),
                (row) => ({
                  ...row,
                  share: areaRevenue > 0 ? (row.revenue / areaRevenue) * 100 : 0,
                })
              ),
            };
          }
        ),
        ["revenue"],
        ["desc"]
      )
    : [];

  // Ventas por hora (hora del cobro), dentro del horario del local.
  const hourOf = (timestamp) => moment.parseZone(timestamp).hour();
  const salesByHour = groupBy(sales, (p) => hourOf(p.created_at));
  const allHours = map(OPERATING_HOURS, (hour) => ({
    hour,
    label: formatHourLabel(hour),
    revenue: sumBy(salesByHour[hour] || [], (p) => Number(p.totalPayment)),
    count: (salesByHour[hour] || []).length,
  }));
  // Ticket promedio por hora (su "Ticket promedio por hora — por cantidad de
  // ventas"). null en horas sin ventas: el gráfico deja el hueco en vez de
  // dibujar un falso S/ 0.
  allHours.forEach((h) => {
    h.avgTicket = h.count > 0 ? h.revenue / h.count : null;
  });
  const first = findIndex(allHours, (h) => h.count > 0);
  const last = findLastIndex(allHours, (h) => h.count > 0);
  // El gráfico se recorta a las horas con actividad (+1 de margen a cada lado).
  const hourly =
    first === -1
      ? []
      : allHours.slice(Math.max(0, first - 1), Math.min(allHours.length, last + 2));
  const activeHours = first === -1 ? [] : allHours.slice(first, last + 1);
  // Hora punta / más libre según el modo del gráfico (como Restaurant.pe:
  // "Dinero por ventas" o "Cantidad de ventas") — no siempre coinciden: una
  // hora con pocas cuentas grandes puede facturar más que una con muchas
  // chicas. "Más libre" = la de menos ventas ENTRE la primera y la última hora
  // con actividad (fuera de ese tramo el local no estaba atendiendo).
  const peakHour = maxBy(activeHours, "revenue");
  const quietHour = activeHours.length > 1 ? minBy(activeHours, "revenue") : null;
  const peakHourByCount = maxBy(activeHours, "count");
  const quietHourByCount = activeHours.length > 1 ? minBy(activeHours, "count") : null;

  // Tiempo en mesa: primer pedido de la cuenta → cobro (`paid_at`, como el
  // "Tiempo promedio atención en mesa" de Restaurant.pe). Los pagos de antes
  // de que existiera paid_at se rellenaron con created_at (= cuándo se pidió
  // la cuenta): para esos sigue siendo una aproximación. iCard no guarda la
  // hora de llegada de los comensales, solo la del primer pedido.
  const ordersByPayment = groupBy(soldOrders, "payment");
  const stays = filter(
    map(sales, (p) => {
      const paymentOrders = ordersByPayment[p.id];
      if (!paymentOrders) return null;
      const firstOrder = minBy(paymentOrders, (o) => moment(o.created_at).valueOf());
      const end = p.paid_at || p.created_at;
      return moment(end).diff(moment(firstOrder.created_at), "minutes");
    }),
    (minutes) => minutes != null && minutes >= 0
  );

  // Tiempo de despacho (su "Frecuencia de atención de pedidos"): pedido →
  // cocina lo marcó "Listo" en su pantalla. Mismo cálculo que el reporte
  // Tiempos de Cocina (utils/prepTimes.js), aquí solo promedio / mín / máx.
  const dispatchMinutes = filter(
    map(
      filter(orders, (o) => o.ready_at),
      prepMinutes
    ),
    (minutes) => minutes >= 0
  );
  const dispatch = dispatchMinutes.length
    ? {
        count: dispatchMinutes.length,
        average: mean(dispatchMinutes),
        min: Math.min(...dispatchMinutes),
        max: Math.max(...dispatchMinutes),
      }
    : null;

  // Ranking de mozos (mismo criterio que Restaurant.pe): cada venta se
  // atribuye a quien registró su PRIMER pedido. Sin usuario = el comensal
  // pidió solo desde el QR.
  const waiterOf = (paymentOrders) => {
    const firstOrder = minBy(paymentOrders, (o) => moment(o.created_at).valueOf());
    return firstOrder?.created_by_name || "Autoservicio (QR)";
  };
  const salesByWaiter = groupBy(
    filter(sales, (p) => ordersByPayment[p.id]),
    (p) => waiterOf(ordersByPayment[p.id])
  );
  const waiterRanking = orderBy(
    map(salesByWaiter, (waiterSales, name) => ({
      name,
      salesCount: waiterSales.length,
      revenue: sumBy(waiterSales, (p) => Number(p.totalPayment)),
      share: pct(sumBy(waiterSales, (p) => Number(p.totalPayment))),
    })),
    ["revenue"],
    ["desc"]
  );
  // "Mozo del día": el de más ventas entre el personal (no el QR).
  const topWaiter = waiterRanking.find((w) => w.name !== "Autoservicio (QR)") || null;

  return {
    totalSales,
    salesCount,
    avgTicket: salesCount > 0 ? totalSales / salesCount : 0,
    cashTotal,
    cardTotal,
    cashPct: pct(cashTotal),
    cardPct: pct(cardTotal),
    byMethod,
    tablesServed: uniq(filter(map(sales, "table_number"), (n) => n != null)).length,
    lossTotal: sumBy(losses, (p) => Number(p.totalPayment)),
    // Afluencia (su "Ticket promedio por hora (Afluencia)"): solo con las
    // cuentas que tienen el número de personas marcado (Payment.guests) —
    // mezclar las que no lo tienen falsearía el promedio.
    ...(() => {
      const withGuests = filter(sales, (p) => Number(p.guests) > 0);
      const guestsTotal = sumBy(withGuests, (p) => Number(p.guests));
      // Solo el salón pregunta cuántas personas son: WhatsApp, Venta rápida y
      // Delivery no cuentan en el "de N cuentas" (no es que faltó marcarlas).
      const salonSalesCount = filter(
        sales,
        (p) => (p.channel || "SALON") === "SALON"
      ).length;
      return {
        guestsTotal,
        guestsSalesCount: withGuests.length,
        salonSalesCount,
        ticketPerPerson:
          guestsTotal > 0
            ? sumBy(withGuests, (p) => Number(p.totalPayment)) / guestsTotal
            : null,
      };
    })(),
    // Por canal de venta (Payment.channel: tipo de mesa al crear la cuenta).
    // Pagos anteriores a este campo quedaron como Salón (default).
    byChannel: map(TABLE_KINDS, (kind) => {
      const channelSales = filter(sales, (p) => (p.channel || "SALON") === kind.value);
      return {
        ...kind,
        count: channelSales.length,
        total: sumBy(channelSales, (p) => Number(p.totalPayment)),
      };
    }),
    // Por ambiente (Salón, Terraza…): solo ventas del salón, con la FOTO del
    // ambiente al cobrar (Payment.area_name). Sin ambiente (ventas viejas) no
    // se cuentan. La tarjeta solo se muestra con dos o más ambientes.
    byPlace: orderBy(
      map(
        groupBy(
          filter(sales, (p) => (p.channel || "SALON") === "SALON" && p.area_name),
          "area_name"
        ),
        (list, name) => {
          const total = sumBy(list, (p) => Number(p.totalPayment));
          return { name, count: list.length, total, ticket: total / list.length };
        }
      ),
      "total",
      "desc"
    ),
    // Delivery por app (Payment.delivery_channel) y total cobrado en envíos.
    deliveryByChannel: filter(
      map(DELIVERY_CHANNELS, (channel) => {
        const channelSales = filter(
          sales,
          (p) => p.channel === "DELIVERY" && p.delivery_channel === channel.value
        );
        return {
          ...channel,
          count: channelSales.length,
          total: sumBy(channelSales, (p) => Number(p.totalPayment)),
        };
      }),
      (row) => row.count > 0
    ),
    deliveryFeeTotal: sumBy(sales, (p) => Number(p.delivery_fee || 0)),
    // Descuentos y cortesías aplicados al cobrar (las ventas ya los restan).
    discountTotal: sumBy(sales, (p) => Number(p.discount_amount || 0)),
    discountCount: filter(sales, (p) => Number(p.discount_amount || 0) > 0).length,
    // Cortesías por plato (Order.is_courtesy, valor en courtesy_value).
    ...(() => {
      const courtesies = filter(
        orders,
        (o) => o.is_courtesy && o.payment_type !== "LOSS" && o.payment_status !== "VOID"
      );
      return {
        courtesyTotal: sumBy(courtesies, (o) => Number(o.courtesy_value || 0)),
        courtesyCount: courtesies.length,
      };
    })(),
    lossCount: losses.length,
    avgStayMinutes: stays.length ? Math.round(mean(stays)) : null,
    dispatch,
    itemsSold: soldOrders.length,
    hourly,
    peakHour,
    quietHour,
    peakHourByCount,
    quietHourByCount,
    topByQuantity,
    topByRevenue,
    byCategory,
    waiterRanking,
    // "Más ventas para" (Restaurant.pe): ventas por cliente de la cuenta
    // (Payment.customer). Sin cliente = "Sin cliente" (su "Cliente genérico").
    byCustomer: orderBy(
      map(
        groupBy(sales, (p) => p.customer_data?.full_name || "Sin cliente"),
        (customerSales, name) => {
          const revenue = sumBy(customerSales, (p) => Number(p.totalPayment));
          return { title: name, quantity: customerSales.length, revenue, share: pct(revenue) };
        }
      ),
      ["revenue"],
      ["desc"]
    ).slice(0, 10),
    byArea,
    topWaiter,
  };
}
